(function () {
  const ADD_TO_CART_LABEL = 'Add to cart';
  const ADDING_LABEL = 'Adding...';
  const ADDED_LABEL = 'Added to cart';
  const SOLD_OUT_LABEL = 'Sold out';

  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  class WishlistDrawer extends HTMLElement {
    constructor() {
      super();
      this.swat = null;
      this.selectedListId = null;
      this.activeElement = null;
      this.isRendering = false;
    }

    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.loader = this.querySelector('#WishlistDrawer-Loader');
      this.itemsContainer = this.querySelector('#WishlistDrawer-Items');
      this.emptyState = this.querySelector('#WishlistDrawer-Empty');
      this.overlay = this.querySelector('#WishlistDrawer-Overlay');

      this.addEventListener('keyup', (event) => {
        if (event.code === 'Escape') this.close();
      });

      this.addEventListener('click', (event) => {
        const inner = this.querySelector('.wishlist-drawer__inner');
        if (inner && !inner.contains(event.target)) {
          this.close();
        }
      });

      this.overlay?.addEventListener('click', (event) => {
        event.stopPropagation();
        this.close();
      });

      this.querySelector('.wishlist-drawer__inner')?.addEventListener('click', (event) => {
        event.stopPropagation();
      });

      this.querySelectorAll('[data-wishlist-drawer-close]').forEach((button) => {
        button.addEventListener('click', () => this.close());
      });

      this.initSwym();
      this.bindHeaderTrigger();
    }

    initSwym() {
      window.SwymCallbacks = window.SwymCallbacks || [];
      window.SwymCallbacks.push((swat) => {
        this.swat = swat;
        this.bindSwymEvents(swat);
      });

      if (window._swat) {
        this.swat = window._swat;
        this.bindSwymEvents(window._swat);
      }
    }

    bindSwymEvents(swat) {
      if (!swat.evtLayer || this.dataset.swymEventsBound === 'true') return;
      this.dataset.swymEventsBound = 'true';

      const refresh = () => {
        this.updateHeaderCount();
        if (this.classList.contains('active')) {
          this.renderWishlist();
        }
      };

      swat.evtLayer.addEventListener('sw:addedtowishlist', refresh);
      swat.evtLayer.addEventListener('sw:removedfromwishlist', refresh);
      this.updateHeaderCount();
    }

    bindHeaderTrigger() {
      document.addEventListener(
        'click',
        (event) => {
          const trigger = event.target.closest('.swym-advanced-header-icon, .swym-advanced-mobile, #swym-advanced-header-icon');
          if (!trigger) return;

          event.preventDefault();
          event.stopImmediatePropagation();
          this.open(trigger);
        },
        true
      );
    }

    open(triggeredBy) {
      if (triggeredBy) this.activeElement = triggeredBy;

      window.setTimeout(() => {
        this.classList.add('active');
      }, 0);

      this.setAttribute('aria-hidden', 'false');
      document.body.classList.add('overflow-hidden');
      this.renderWishlist();

      window.setTimeout(() => {
        const focusTarget = this.querySelector('.wishlist-drawer__close') || this.querySelector('.wishlist-drawer__inner');
        if (typeof trapFocus === 'function' && focusTarget) {
          trapFocus(this.querySelector('.wishlist-drawer__inner'), focusTarget);
        } else {
          focusTarget?.focus();
        }
      }, 100);
    }

    close() {
      this.classList.remove('active');
      this.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('overflow-hidden');

      if (typeof removeTrapFocus === 'function') {
        removeTrapFocus(this.activeElement);
      }
    }

    getSwat() {
      return this.swat || window._swat || null;
    }

    showLoader() {
      if (this.loader) this.loader.hidden = false;
    }

    hideLoader() {
      if (this.loader) this.loader.hidden = true;
    }

    updateUIState(items) {
      const hasItems = items && items.length > 0;

      if (this.itemsContainer) {
        this.itemsContainer.hidden = !hasItems;
      }

      if (this.emptyState) {
        this.emptyState.hidden = hasItems;
      }

      this.updateHeaderCount(items ? items.length : undefined);
    }

    updateHeaderCount(explicitCount) {
      const swat = this.getSwat();
      const countNodes = document.querySelectorAll('.swym-header-icon-count, [data-swym-count]');

      const applyCount = (count) => {
        countNodes.forEach((node) => {
          node.textContent = count > 0 ? String(count) : '';
          node.setAttribute('data-count', String(count));
        });
      };

      if (typeof explicitCount === 'number') {
        applyCount(explicitCount);
        return;
      }

      if (!swat) return;

      if (typeof swat.fetchLists === 'function') {
        swat.fetchLists({
          callbackFn: (lists) => {
            const list = lists && lists.length ? lists[0] : null;
            applyCount(list?.listcontents?.length || 0);
          },
          errorFn: () => {
            if (typeof swat.fetch === 'function') {
              swat.fetch((items) => applyCount(items ? items.length : 0));
            }
          }
        });
        return;
      }

      if (typeof swat.fetch === 'function') {
        swat.fetch((items) => applyCount(items ? items.length : 0));
      }
    }

    fetchWishlistItems() {
      const swat = this.getSwat();

      return new Promise((resolve) => {
        if (!swat) {
          resolve([]);
          return;
        }

        if (typeof swat.fetchLists === 'function') {
          swat.fetchLists({
            callbackFn: (lists) => {
              const selectedList = lists && lists.length ? lists[0] : null;
              this.selectedListId = selectedList?.lid || null;
              resolve(selectedList?.listcontents || []);
            },
            errorFn: () => resolve([])
          });
          return;
        }

        if (typeof swat.fetch === 'function') {
          swat.fetch((items) => resolve(items || []));
          return;
        }

        resolve([]);
      });
    }

    async fetchProductData(listItem) {
      const productUrl = listItem.du ? listItem.du.split('?')[0] : null;
      if (!productUrl) return null;

      try {
        const response = await fetch(`${productUrl}.js`, { credentials: 'same-origin' });
        if (!response.ok) return null;
        return await response.json();
      } catch (error) {
        return null;
      }
    }

    getActiveCurrency() {
      if (window.Shopify?.currency?.active) {
        return window.Shopify.currency.active;
      }

      if (this.dataset.currency) {
        return this.dataset.currency;
      }

      return window.ShopifyCurrency || 'GBP';
    }

    getActiveLocale() {
      return window.ShopifyLocale || this.dataset.locale || 'en-GB';
    }

    convertShopCurrencyCents(cents) {
      const rate = Number(window.Shopify?.currency?.rate || 1);
      if (!Number.isFinite(rate) || rate === 1) {
        return Math.round(cents);
      }

      return Math.round(cents * rate);
    }

    getPriceCents(variant, item) {
      if (variant?.price != null) {
        return Number(variant.price);
      }

      if (item?.pr) {
        return this.convertShopCurrencyCents(Number(item.pr) * 100);
      }

      return null;
    }

    formatMoney(cents) {
      const amount = Number(cents) / 100;
      const currency = this.getActiveCurrency();
      const locale = this.getActiveLocale();

      try {
        return new Intl.NumberFormat(locale, {
          style: 'currency',
          currency,
          minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
          maximumFractionDigits: 2
        }).format(amount);
      } catch (error) {
        const swat = this.getSwat();
        const symbol = swat?.currency || '£';
        return `${symbol}${amount.toFixed(amount % 1 === 0 ? 0 : 2)}`;
      }
    }

    getMaterialLabel(productData, variant) {
      if (variant?.option1 && variant.option1 !== 'Default Title') {
        return variant.option1;
      }

      if (productData?.type) {
        return productData.type;
      }

      return '';
    }

    buildMetaText(material, price) {
      if (material && price) return `${material} | ${price}`;
      return material || price || '';
    }

    removeFromWishlist(item) {
      const swat = this.getSwat();
      if (!swat) return Promise.reject(new Error('Wishlist unavailable'));

      const product = {
        epi: item.epi,
        empi: item.empi,
        du: item.du
      };

      return new Promise((resolve, reject) => {
        const onSuccess = (response) => resolve(response);
        const onError = (error) => reject(error || new Error('Could not remove item'));

        if (this.selectedListId && typeof swat.deleteFromList === 'function') {
          swat.deleteFromList(this.selectedListId, product, onSuccess, onError);
          return;
        }

        if (typeof swat.removeFromWishList === 'function') {
          swat.removeFromWishList(product, onSuccess, onError);
          return;
        }

        reject(new Error('Wishlist unavailable'));
      });
    }

    addToCart(item, button) {
      const swat = this.getSwat();
      if (!swat || typeof swat.replayAddToCart !== 'function') return;

      const originalLabel = button.innerHTML;
      button.disabled = true;
      button.innerHTML = `${ADDING_LABEL}<svg width="16" height="16" viewBox="0 0 17 17" fill="none" aria-hidden="true"><path d="M13.5 5.5H11.5V4C11.5 3.20435 11.1839 2.44129 10.6213 1.87868C10.0587 1.31607 9.29565 1 8.5 1C7.70435 1 6.94129 1.31607 6.37868 1.87868C5.81607 2.44129 5.5 3.20435 5.5 4V5.5H3.5C3.23478 5.5 2.98043 5.60536 2.79289 5.79289C2.60536 5.98043 2.5 6.23478 2.5 6.5V13.5C2.5 13.7652 2.60536 14.0196 2.79289 14.2071C2.98043 14.3946 3.23478 14.5 3.5 14.5H13.5C13.7652 14.5 14.0196 14.3946 14.2071 14.2071C14.3946 14.0196 14.5 13.7652 14.5 13.5V6.5C14.5 6.23478 14.3946 5.98043 14.2071 5.79289C14.0196 5.60536 13.7652 5.5 13.5 5.5ZM6.5 4C6.5 3.46957 6.71071 2.96086 7.08579 2.58579C7.46086 2.21071 7.96957 2 8.5 2C9.03043 2 9.53914 2.21071 9.91421 2.58579C10.2893 2.96086 10.5 3.46957 10.5 4V5.5H6.5V4ZM13.5 13.5H3.5V6.5H13.5V13.5ZM9.25 10C9.25 10.1483 9.20601 10.2933 9.1236 10.4167C9.04119 10.54 8.92406 10.6361 8.78701 10.6929C8.64997 10.7497 8.49917 10.7645 8.35368 10.7356C8.2082 10.7066 8.07456 10.6352 7.96967 10.5303C7.86478 10.4254 7.79335 10.2918 7.76441 10.1463C7.73547 10.0008 7.75032 9.85003 7.80709 9.71299C7.86386 9.57594 7.95999 9.45881 8.08332 9.3764C8.20666 9.29399 8.35166 9.25 8.5 9.25C8.69891 9.25 8.88968 9.32902 9.03033 9.46967C9.17098 9.61032 9.25 9.80109 9.25 10Z" fill="currentColor"/></svg>`;

      swat.replayAddToCart(
        { empi: item.empi, du: item.du, qty: item.qty || 1 },
        item.epi,
        () => {
          button.disabled = false;
          button.innerHTML = `${ADDED_LABEL}<svg width="16" height="16" viewBox="0 0 17 17" fill="none" aria-hidden="true"><path d="M13.5 5.5H11.5V4C11.5 3.20435 11.1839 2.44129 10.6213 1.87868C10.0587 1.31607 9.29565 1 8.5 1C7.70435 1 6.94129 1.31607 6.37868 1.87868C5.81607 2.44129 5.5 3.20435 5.5 4V5.5H3.5C3.23478 5.5 2.98043 5.60536 2.79289 5.79289C2.60536 5.98043 2.5 6.23478 2.5 6.5V13.5C2.5 13.7652 2.60536 14.0196 2.79289 14.2071C2.98043 14.3946 3.23478 14.5 3.5 14.5H13.5C13.7652 14.5 14.0196 14.3946 14.2071 14.2071C14.3946 14.0196 14.5 13.7652 14.5 13.5V6.5C14.5 6.23478 14.3946 5.98043 14.2071 5.79289C14.0196 5.60536 13.7652 5.5 13.5 5.5ZM6.5 4C6.5 3.46957 6.71071 2.96086 7.08579 2.58579C7.46086 2.21071 7.96957 2 8.5 2C9.03043 2 9.53914 2.21071 9.91421 2.58579C10.2893 2.96086 10.5 3.46957 10.5 4V5.5H6.5V4ZM13.5 13.5H3.5V6.5H13.5V13.5ZM9.25 10C9.25 10.1483 9.20601 10.2933 9.1236 10.4167C9.04119 10.54 8.92406 10.6361 8.78701 10.6929C8.64997 10.7497 8.49917 10.7645 8.35368 10.7356C8.2082 10.7066 8.07456 10.6352 7.96967 10.5303C7.86478 10.4254 7.79335 10.2918 7.76441 10.1463C7.73547 10.0008 7.75032 9.85003 7.80709 9.71299C7.86386 9.57594 7.95999 9.45881 8.08332 9.3764C8.20666 9.29399 8.35166 9.25 8.5 9.25C8.69891 9.25 8.88968 9.32902 9.03033 9.46967C9.17098 9.61032 9.25 9.80109 9.25 10Z" fill="currentColor"/></svg>`;

          if (typeof refreshCartDrawer === 'function') {
            refreshCartDrawer();
          }

          document.dispatchEvent(new CustomEvent('wishlist:added-to-cart', { detail: { item } }));
        },
        (error) => {
          button.disabled = false;
          button.innerHTML = originalLabel;

          if (swat.ui && typeof swat.ui.showErrorNotification === 'function') {
            swat.ui.showErrorNotification({
              message: (error && error.description) || 'Could not add this item to cart.'
            });
          }
        }
      );
    }

    renderWishlistItem(item, productData) {
      const row = document.createElement('article');
      row.className = 'wishlist-drawer__item';
      row.dataset.epi = item.epi;
      row.dataset.empi = item.empi;

      const variant = productData?.variants?.find((entry) => String(entry.id) === String(item.epi));
      const imageSrc =
        variant?.featured_image?.src ||
        productData?.featured_image ||
        item.iu ||
        '';
      const title = escapeHtml(productData?.title || item.dt || 'Product');
      const material = this.getMaterialLabel(productData, variant);
      const priceCents = this.getPriceCents(variant, item);
      const price = priceCents ? this.formatMoney(priceCents) : '';
      const meta = escapeHtml(this.buildMetaText(material, price));
      const productUrl = escapeHtml(item.du || '#');
      const isAvailable = variant ? variant.available : true;
      const cartIcon = '<svg width="12" height="12" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12.5 6.25C12.5 6.44891 12.421 6.63968 12.2803 6.78033C12.1397 6.92098 11.9489 7 11.75 7H7V11.75C7 11.9489 6.92098 12.1397 6.78033 12.2803C6.63968 12.421 6.44891 12.5 6.25 12.5C6.05109 12.5 5.86032 12.421 5.71967 12.2803C5.57902 12.1397 5.5 11.9489 5.5 11.75V7H0.75C0.551088 7 0.360322 6.92098 0.21967 6.78033C0.0790177 6.63968 0 6.44891 0 6.25C0 6.05109 0.0790177 5.86032 0.21967 5.71967C0.360322 5.57902 0.551088 5.5 0.75 5.5H5.5V0.75C5.5 0.551088 5.57902 0.360322 5.71967 0.21967C5.86032 0.0790177 6.05109 0 6.25 0C6.44891 0 6.63968 0.0790177 6.78033 0.21967C6.92098 0.360322 7 0.551088 7 0.75V5.5H11.75C11.9489 5.5 12.1397 5.57902 12.2803 5.71967C12.421 5.86032 12.5 6.05109 12.5 6.25Z" fill="white"/></svg>';    

      row.innerHTML = `
        <a href="${productUrl}" class="wishlist-drawer__item-image-wrap" tabindex="-1" aria-hidden="true">
          <img class="wishlist-drawer__item-image" src="${escapeHtml(imageSrc)}" alt="" loading="lazy" width="100" height="100">
        </a>
        <div class="wishlist-drawer__item-content">
          <div class="wishlist-drawer__item-top">
            <div class="wishlist-drawer__item-details">
              <a href="${productUrl}" class="wishlist-drawer__item-title">${title}</a>
              ${meta ? `<p class="wishlist-drawer__item-meta">${meta}</p>` : ''}
            </div>
            <button type="button" class="wishlist-drawer__item-remove" data-action="remove">Remove</button>
          </div>
          <button type="button" class="wishlist-drawer__add-to-cart" data-action="add-to-cart" ${isAvailable ? '' : 'disabled'}>
            ${isAvailable ? ADD_TO_CART_LABEL : SOLD_OUT_LABEL}${isAvailable ? cartIcon : ''}
          </button>
        </div>
      `;

      row.querySelector('[data-action="remove"]')?.addEventListener('click', () => {
        this.removeFromWishlist(item)
          .then(() => this.renderWishlist())
          .catch((error) => console.error('[WishlistDrawer] remove failed', error));
      });

      const addButton = row.querySelector('[data-action="add-to-cart"]');
      if (addButton && isAvailable) {
        addButton.addEventListener('click', () => this.addToCart(item, addButton));
      }

      return row;
    }

    async renderWishlist() {
      if (!this.itemsContainer || this.isRendering) return;

      const swat = this.getSwat();
      if (!swat) {
        this.updateUIState([]);
        return;
      }

      this.isRendering = true;
      this.showLoader();

      try {
        const items = await this.fetchWishlistItems();
        const productEntries = await Promise.all(
          items.map(async (item) => {
            const productData = await this.fetchProductData(item);
            return productData ? { item, productData } : null;
          })
        );

        this.itemsContainer.innerHTML = '';
        productEntries
          .filter(Boolean)
          .forEach(({ item, productData }) => {
            this.itemsContainer.appendChild(this.renderWishlistItem(item, productData));
          });

        this.updateUIState(items);
      } catch (error) {
        console.error('[WishlistDrawer] render failed', error);
        this.updateUIState([]);
      } finally {
        this.hideLoader();
        this.isRendering = false;
      }
    }
  }

  if (!customElements.get('wishlist-drawer')) {
    customElements.define('wishlist-drawer', WishlistDrawer);
  }

  window.openWishlistDrawer = function openWishlistDrawer() {
    const drawer = document.querySelector('wishlist-drawer');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
    }
  };

  window.updateHeaderWishlistCount = function updateHeaderWishlistCount() {
    const drawer = document.querySelector('wishlist-drawer');
    if (drawer && typeof drawer.updateHeaderCount === 'function') {
      drawer.updateHeaderCount();
    }
  };

  document.addEventListener('poubel:wishlist-changed', function () {
    window.updateHeaderWishlistCount();
  });

  function closeMobileMenuDrawer() {
    const details = document.querySelector('#Details-menu-drawer-container');
    const headerDrawer = document.querySelector('header-drawer');
    if (!details || !details.hasAttribute('open')) return;

    const summary = details.querySelector('summary');
    document.body.classList.remove('overflow-hidden-tablet');
    details.classList.remove('menu-opening');
    document.querySelector('.section-header')?.classList.remove('menu-open');

    if (headerDrawer && summary && typeof headerDrawer.closeMenuDrawer === 'function') {
      headerDrawer.closeMenuDrawer(new Event('click'), summary);
      return;
    }

    details.removeAttribute('open');
  }

  function closeDesktopMegamenu() {
    document.body.classList.remove('desktop-megamenu-active');
    document
      .querySelectorAll('.mega-menu__item.active, .desktop__megamenu.active, .desktop-megamenu__item.active')
      .forEach(function (element) {
        element.classList.remove('active');
      });
  }

  document.addEventListener('click', function (event) {
    const trigger = event.target.closest('[data-wishlist-drawer-open]');
    if (!trigger) return;

    event.preventDefault();
    closeMobileMenuDrawer();
    closeDesktopMegamenu();
    window.openWishlistDrawer();
  });
})();
